import { useMemo } from 'react';
import { Alert, Autocomplete, Stack, TextField } from '@mui/material';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { generateGuid } from 'guid';
import { useFormContext } from 'FrontRoyalReactHookForm';
import { SectionHeading, SelectedChipList, preventBackspaceRemoval } from 'ContentItemEditor';
import { streamEditorApi } from './streamEditorApi';
import { type ContentTopic, type StreamFormValues } from './StreamEditor.types';

type Props = {
    // Topic labels are per locale, so the list offered is the one for this course's locale.
    locale: string;
};

type TopicOption = { topic: ContentTopic; label: string };

/*
    The topics this course covers. They live on the locale pack, so every translation shares them,
    and only the English course may add one: ContentTopic.update_topics reads locales.en.
*/
export default function ContentTopicsField({ locale }: Props) {
    const { watch, setFieldValue } = useFormContext<StreamFormValues>();
    const { data: topics, isLoading, isError } = streamEditorApi.useGetContentTopicsQuery();

    // The raw value is the dependency. `watch(...) || []` builds a new array each render, so the
    // memo below never held.
    const watched = watch('content_topics');
    const chosen = useMemo(() => watched || [], [watched]);
    const chosenIds = useMemo(() => new Set(chosen.map(topic => topic.id)), [chosen]);

    // A topic with no label in this locale has nothing to show, so it is not offered.
    const options = useMemo<TopicOption[]>(
        () =>
            (topics || [])
                .filter(topic => !!topic.locales[locale] && !chosenIds.has(topic.id))
                .map(topic => ({ topic, label: topic.locales[locale] })),
        [topics, locale, chosenIds],
    );

    function addTypedTopic(label: string) {
        const trimmed = label.trim();
        if (!trimmed) return;

        setFieldValue('content_topics', [...chosen, { id: generateGuid(), locales: { [locale]: trimmed } }]);
    }

    if (isError) {
        return <Alert severity="error">Could not load the list of topics. Reload the page to try again.</Alert>;
    }

    if (isLoading) return <FrontRoyalSpinner className="inline" />;

    return (
        <Stack spacing={1}>
            <SectionHeading>Key Topics Covered</SectionHeading>

            <Autocomplete
                freeSolo
                // Choosing a topic appends it to the list below and clears the input.
                value={null}
                blurOnSelect
                options={options}
                getOptionLabel={option => (typeof option === 'string' ? option : option.label)}
                isOptionEqualToValue={(option, selected) =>
                    typeof option !== 'string' && typeof selected !== 'string' && option.topic.id === selected.topic.id
                }
                onChange={(_event, option) => {
                    if (!option) return;

                    // A string means the admin typed a label that matches no topic.
                    if (typeof option === 'string') {
                        addTypedTopic(option);
                        return;
                    }

                    setFieldValue('content_topics', [...chosen, option.topic]);
                }}
                onKeyDown={preventBackspaceRemoval}
                // Keyed by id, because MUI keys an option by its label and two topics can share
                // one. A string option is the label the admin typed, which is its own key.
                // Its own render, not the shared one: a freeSolo option may be the string the admin
                // typed, which has no id.
                renderOption={({ key: _key, ...optionProps }, option) => (
                    <li key={typeof option === 'string' ? option : option.topic.id} {...optionProps}>
                        {typeof option === 'string' ? option : option.label}
                    </li>
                )}
                renderInput={props => <TextField {...props} name="topics" label="Topics" />}
            />

            <SelectedChipList
                items={chosen.map(topic => ({ key: topic.id, label: topic.locales[locale] || topic.id }))}
                onRemove={id =>
                    setFieldValue(
                        'content_topics',
                        chosen.filter(topic => topic.id !== id),
                    )
                }
            />
        </Stack>
    );
}
